import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircle, Users, Lock, RefreshCw, Loader2, ChevronRight, Search, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PersonAccordion({ personName, personId, groups, togglingId, onToggle, onBulkToggle, token }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const activeCount = groups.filter(g => !g.excluded).length;
  const excludedCount = groups.length - activeCount;

  const filtered = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.toLowerCase();
    return groups.filter(g => (g.group_name || '').toLowerCase().includes(q));
  }, [groups, search]);

  const handleBulk = async (exclude) => {
    setBulkLoading(true);
    try {
      await onBulkToggle(personId, exclude);
      toast.success(exclude
        ? `Tous les groupes de ${personName} désactivés`
        : `Tous les groupes de ${personName} activés`
      );
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`wa-person-${personId}`}>
      {/* Accordion header */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
        data-testid={`wa-person-toggle-${personId}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform shrink-0 ${open ? 'rotate-90' : ''}`} />
          <Users className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="text-sm font-medium truncate">{personName}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          <Badge variant="outline" className="text-[10px]">{groups.length} groupes</Badge>
          <Badge variant="secondary" className="text-[10px] text-green-600">{activeCount} actifs</Badge>
          {excludedCount > 0 && (
            <Badge variant="destructive" className="text-[10px]">{excludedCount} exclus</Badge>
          )}
        </div>
      </button>

      {/* Accordion content */}
      {open && (
        <div className="border-t px-3 pb-3">
          {/* Toolbar: search + bulk actions */}
          <div className="flex items-center gap-2 py-3 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Rechercher un groupe..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid={`wa-search-${personId}`}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulk(true)}
              disabled={bulkLoading || excludedCount === groups.length}
              className="text-xs h-8"
              data-testid={`wa-bulk-disable-${personId}`}
            >
              {bulkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
                <><ToggleLeft className="w-3.5 h-3.5 mr-1" /> Tout désactiver</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulk(false)}
              disabled={bulkLoading || activeCount === groups.length}
              className="text-xs h-8"
              data-testid={`wa-bulk-enable-${personId}`}
            >
              {bulkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
                <><ToggleRight className="w-3.5 h-3.5 mr-1" /> Tout activer</>
              )}
            </Button>
          </div>

          {/* Group list */}
          <div className="space-y-0.5 max-h-[350px] overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucun groupe trouvé</p>
            ) : (
              filtered.map(g => (
                <div
                  key={`${g.person_id}-${g.group_id}`}
                  className="flex items-center justify-between py-2 px-2 rounded-md hover:bg-muted/50 transition-colors"
                  data-testid={`wa-admin-group-${g.group_id}`}
                >
                  <div className="space-y-0.5 min-w-0 flex-1 mr-4">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{g.group_name || 'Inconnu'}</p>
                      {g.excluded_by_admin && (
                        <Badge variant="destructive" className="text-[10px] gap-1 shrink-0">
                          <Lock className="w-2.5 h-2.5" /> Admin
                        </Badge>
                      )}
                      {g.excluded && !g.excluded_by_admin && (
                        <Badge variant="secondary" className="text-[10px] shrink-0">
                          Exclu par utilisateur
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{g.participant_count || 0} participants</p>
                  </div>
                  <Switch
                    checked={!g.excluded}
                    disabled={togglingId === g.group_id}
                    onCheckedChange={() => onToggle(g.group_id)}
                    data-testid={`wa-admin-toggle-${g.group_id}`}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function WhatsAppGroupsAdmin({ token }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tenants/whatsapp-groups`, { headers });
      setGroups(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); }, [token]);

  const handleAdminToggle = async (groupId) => {
    setTogglingId(groupId);
    try {
      const { data } = await axios.put(
        `${API}/tenants/whatsapp-groups/${encodeURIComponent(groupId)}/admin-toggle`,
        {},
        { headers }
      );
      setGroups(prev => prev.map(g =>
        g.group_id === groupId
          ? { ...g, excluded_by_admin: data.excluded_by_admin, excluded: data.excluded }
          : g
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setTogglingId(null);
    }
  };

  const handleBulkToggle = async (personId, exclude) => {
    try {
      await axios.put(
        `${API}/tenants/whatsapp-groups/bulk-toggle/${personId}?exclude=${exclude}`,
        {},
        { headers }
      );
      setGroups(prev => prev.map(g =>
        g.person_id === personId
          ? { ...g, excluded_by_admin: exclude, excluded: exclude }
          : g
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur bulk toggle');
    }
  };

  // Group by person
  const byPerson = useMemo(() => {
    const map = {};
    for (const g of groups) {
      const name = g.person_name || 'Inconnu';
      const pid = g.person_id || 'unknown';
      if (!map[pid]) map[pid] = { name, groups: [] };
      map[pid].groups.push(g);
    }
    return Object.entries(map);
  }, [groups]);

  const totalGroups = groups.length;
  const totalActive = groups.filter(g => !g.excluded).length;
  const totalExcluded = totalGroups - totalActive;

  if (loading) {
    return (
      <Card data-testid="wa-groups-admin-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10"><MessageCircle className="w-5 h-5 text-green-500" /></div>
            <div>
              <CardTitle className="text-base font-heading">Groupes WhatsApp</CardTitle>
              <CardDescription className="text-xs">Chargement...</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (groups.length === 0) {
    return (
      <Card data-testid="wa-groups-admin-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10"><MessageCircle className="w-5 h-5 text-green-500" /></div>
            <div>
              <CardTitle className="text-base font-heading">Groupes WhatsApp</CardTitle>
              <CardDescription className="text-xs">Gestion globale des groupes synchronisés</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Aucun groupe WhatsApp détecté. Les groupes apparaîtront après la connexion d'une session WhatsApp.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="wa-groups-admin-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10"><MessageCircle className="w-5 h-5 text-green-500" /></div>
            <div>
              <CardTitle className="text-base font-heading">Groupes WhatsApp</CardTitle>
              <CardDescription className="text-xs">
                {totalGroups} groupes · {totalActive} actifs · {totalExcluded} exclus
              </CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={fetchGroups} data-testid="wa-groups-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {byPerson.map(([personId, { name, groups: pGroups }]) => (
            <PersonAccordion
              key={personId}
              personId={personId}
              personName={name}
              groups={pGroups}
              togglingId={togglingId}
              onToggle={handleAdminToggle}
              onBulkToggle={handleBulkToggle}
              token={token}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
